import { useState } from 'react';
import { useSelectStep } from '~/routes/workflowStepParam';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTasksSegmentation } from '~/backend/connector-next-api/connector-next-components';
import { useCurrentUserName } from '~/hooks/domain/useCurrentUserName';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { autoSegmentationTaskState } from '~/state/atoms/structureSet/autoSegmentationTaskAtom';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useCheckTaskInputs from '../useCheckTaskInputs';
import { handleTaskStartError, requireTaskId } from '../utils';

type UseAutoSegmentationTaskProps = {
    studyId: string;
    seriesId: string;
    patientId: string;
    structureSetSopInstanceUid?: string;
};

/**
 * A hook for creating an auto-segmentation task via Connector API.
 * @param {UseAutoSegmentationTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useAutoSegmentationTask({
    studyId,
    seriesId,
    patientId,
    structureSetSopInstanceUid,
}: UseAutoSegmentationTaskProps) {
    const autoSegmentationTaskOptions = useRecoilValue(
        autoSegmentationTaskState
    );
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectStep = useSelectStep();
    const { sessionId } = useParams<{ sessionId?: string }>();

    const { mutateAsync: startSegmentation } = useTasksSegmentation();
    const checkTaskInputs = useCheckTaskInputs();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const currentUserName = useCurrentUserName();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            // The job id is the connector task id: a saved result names it,
            // and the connector records the set's origin from that task.
            const generationModel = transformTaskToGenerationModel(
                job.task,
                job.id
            );
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                setEnvironmentStructureSet(
                    processedItems.structureSets,
                    patientId
                );
            }
        }
    };

    /**
     * Creates the auto-segmentation task with the provided model.
     * @param {PredictionModel} selectedModel - The model to use for the segmentation task.
     */
    const start = async (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            const inputs = await checkTaskInputs({
                patientId,
                seriesInstanceUids: [seriesId],
                structureSetSopInstanceUids:
                    structureSetSopInstanceUid &&
                    autoSegmentationTaskOptions?.includeStructureSet
                        ? [structureSetSopInstanceUid]
                        : [],
            });

            const modelIdentifier = customizationName
                ? `${selectedModel.name}.${customizationName}`
                : selectedModel.name;

            const task = await startSegmentation({
                body: {
                    modelName: modelIdentifier,
                    modelVersion: selectedModel.version,
                    modelLabel: selectedModel.label,
                    customizationName,
                    clientUserName: currentUserName,
                    imageSeries: {
                        patientID: patientId,
                        seriesInstanceUID: seriesId,
                        studyInstanceUID: studyId,
                    },
                    ...(selectedStructures?.length && {
                        rois: selectedStructures,
                    }),
                    ...(structureSetSopInstanceUid &&
                        patientId &&
                        autoSegmentationTaskOptions?.includeStructureSet && {
                            structureSet: {
                                sopInstanceUID: inputs.storedStructureSetUid(
                                    structureSetSopInstanceUid
                                ),
                                patientID: patientId,
                            },
                        }),
                },
            });

            const newJobDetails: NewJobData = {
                id: requireTaskId(task),
                name: 'Contour+',
                type: WorkflowTypes.CONTOUR,
                onLoad: handleProcessedFiles,
                task: {
                    model: selectedModel,
                    customizationName,
                    subSelectedStructures: selectedStructures,
                    output: task,
                    includeStructureSet:
                        autoSegmentationTaskOptions?.includeStructureSet,
                },
            };

            addJob(newJobDetails);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));

            if (sessionId) {
                selectStep('contouring');
            }
        } catch (error) {
            handleTaskStartError(
                error,
                'Failed to start auto-contouring task.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
